import { useEffect, useRef, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { homeFor, useAuth } from '../auth.jsx';
import AuthLayout from '../components/AuthLayout.jsx';
import Alert from '../components/ui/Alert.jsx';
import PasswordField from '../components/PasswordField.jsx';

const MIN_PASSWORD = 12;

/** Step 2: the emailed code + the new password. Success signs the person in. */
export default function ResetPassword() {
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const email = location.state?.email || new URLSearchParams(location.search).get('email') || '';

  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [errors, setErrors] = useState({});
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);
  const [resendAt, setResendAt] = useState(Date.now() + 1000 * (location.state?.resend_after ?? 60));
  const [now, setNow] = useState(Date.now());
  const codeRef = useRef(null);
  const done = useRef(false);

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  useEffect(() => { codeRef.current?.focus(); }, []);

  if (auth.user && !done.current) return <Navigate to={homeFor(auth)} replace />;
  if (!email) return <Navigate to="/forgot-password" replace />;

  const submit = async (e) => {
    e.preventDefault();
    const local = {};
    if (!/^\d{6}$/.test(code)) local.code = 'Enter the 6-digit code from the email.';
    if (password.length < MIN_PASSWORD) local.password = `Use at least ${MIN_PASSWORD} characters.`;
    else if (password.length > 200) local.password = 'Use 200 characters or fewer.';
    else if (password !== confirm) local.password_confirm = 'The passwords don’t match.';
    setErrors(local); setError(''); setNotice('');
    if (Object.keys(local).length) return;
    setBusy(true);
    try {
      done.current = true;
      const me = await auth.resetPassword({ email, code, password, password_confirm: confirm });
      navigate(homeFor(me), { replace: true });
    } catch (err) {
      done.current = false;
      // A problem with the code goes under the code box; field problems by their fields
      if (['invalid_code', 'expired', 'too_many_attempts', 'no_code'].includes(err.code)) {
        setErrors({ ...(err.data?.fields || {}), code: err.message });
        setError('');
        setCode(''); codeRef.current?.focus();
      } else {
        setErrors(err.data?.fields || {});
        setError(err.data?.fields ? '' : err.message);
      }
      setBusy(false);
    }
  };

  const resend = async () => {
    setError(''); setNotice('');
    try {
      const res = await auth.forgotPassword(email);
      setResendAt(Date.now() + 1000 * (res.resend_after ?? 60));
      setCode('');
      setNotice(`If ${email} has an account, we’ve sent it a new code. Earlier codes no longer work.`);
      codeRef.current?.focus();
    } catch (err) {
      setError(err.message);
    }
  };

  const resendIn = Math.max(0, Math.ceil((resendAt - now) / 1000));
  const matches = confirm && password === confirm;

  return (
    <AuthLayout onSubmit={submit}>
      <h1>Set a new password</h1>
      <p className="muted">If <strong className="ink">{email}</strong> has a SummitShield account, we&rsquo;ve emailed it a 6-digit code. It expires in 15 minutes.</p>

      {notice && <Alert tone="info">{notice}</Alert>}
      {error && <Alert tone="danger">{error}</Alert>}

      <div className="field">
        <label htmlFor="reset-code">Code from the email</label>
        <input id="reset-code" ref={codeRef} className="otp-input" value={code}
          onChange={(e) => { setCode(e.target.value.replace(/\D/g, '').slice(0, 6)); if (errors.code) setErrors({ ...errors, code: undefined }); }}
          inputMode="numeric" autoComplete="one-time-code" pattern="\d{6}" maxLength={6} placeholder="••••••"
          aria-invalid={errors.code ? 'true' : undefined} aria-describedby={errors.code ? 'reset-code-err' : undefined} disabled={busy} />
        {errors.code && <p id="reset-code-err" className="field-error">{errors.code}</p>}
      </div>

      <PasswordField label="New password" autoComplete="new-password" required value={password}
        onChange={(e) => { setPassword(e.target.value); if (errors.password) setErrors({ ...errors, password: undefined }); }}
        error={errors.password} hint={!errors.password ? `At least ${MIN_PASSWORD} characters. A short phrase is easiest to remember.` : undefined} />
      <PasswordField label="Confirm new password" autoComplete="new-password" required value={confirm}
        onChange={(e) => { setConfirm(e.target.value); if (errors.password_confirm) setErrors({ ...errors, password_confirm: undefined }); }}
        error={errors.password_confirm} hint={matches && !errors.password_confirm ? '✓ Passwords match' : undefined} />

      <button type="submit" className="btn btn-primary btn-block" disabled={busy}>{busy ? 'Saving…' : 'Set password and sign in'}</button>
      <p className="muted small">This signs you out on every other device.</p>

      <div className="otp-actions">
        <button type="button" className="link-button" onClick={resend} disabled={resendIn > 0}>
          {resendIn > 0 ? `Send a new code in ${resendIn}s` : 'Send a new code'}
        </button>
        <Link to="/forgot-password" state={{ email }}>Use a different email</Link>
      </div>
      <p className="muted small">Can&rsquo;t see it? Check your spam or junk folder. The email comes from SummitShield.</p>
    </AuthLayout>
  );
}
